/**
 * Product tour steps for the Home dashboard (PT-BR).
 * `meta` controls sidebar / Documentos / CRM Neo Documentos during the tour.
 */
export function buildTutorialSteps() {
  return [
    {
      element: '[data-tour="tour-brand"]',
      meta: { requiresSidebar: true },
      popover: {
        title: 'Bem-vindo ao neo-documentos',
        description:
          'Este tour rápido mostra as principais funções do sistema — documentos, biblioteca de modelos, o <strong>CRM Neo Documentos</strong>, a <strong>conexão com WhatsApp</strong> e o atalho para <strong>baixar a nossa extensão</strong> — para você começar com segurança. Use <strong>Próximo passo</strong> para avançar ou <strong>Pular tutorial</strong> se preferir explorar sozinho. Você pode reabrir o tour a qualquer momento pelo botão <strong>Mostrar tutorial</strong> na Home.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="tour-home"]',
      meta: { requiresSidebar: true },
      popover: {
        title: 'Home',
        description:
          'A <strong>Home</strong> é o seu painel inicial. Aqui você vê o resumo da conta, da licença ou da <strong>assinatura mensal</strong> (quando houver), o saldo de Neo Coins, as métricas de uso e a lista de documentos — rascunhos, PDFs e assinaturas. Também há o atalho <strong>Conectar WhatsApp</strong> para vincular seu número ao assistente.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="tour-documents"]',
      meta: { requiresSidebar: true, requiresDocumentsOpen: true },
      popover: {
        title: 'Documentos',
        description:
          'O menu <strong>Documentos</strong> concentra a criação e gestão de contratos. Ao expandir, você acessa três caminhos: criar documento do zero com IA, abrir a <strong>Biblioteca de Modelos</strong> (sistema e personalizados) ou enviar PDFs prontos para assinatura.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="tour-write-contract"]',
      meta: { requiresSidebar: true, requiresDocumentsOpen: true },
      popover: {
        title: 'Criar Documento',
        description:
          'Use <strong>Criar Documento</strong> quando quiser redigir com liberdade no editor. Você conta com o assistente <strong>NeoAi</strong> para ajudar a escrever, revisar e estruturar o texto — ideal para documentos sob medida que não seguem um modelo fixo.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="tour-model-library"]',
      meta: { requiresSidebar: true, requiresDocumentsOpen: true },
      popover: {
        title: 'Biblioteca de Modelos',
        description:
          'A <strong>Biblioteca de Modelos</strong> é o ponto de partida para gerar documentos a partir de templates. Lá você encontra os <strong>modelos do sistema</strong> (organizados por categoria profissional), seus <strong>modelos personalizados</strong> e os favoritos. Escolha um modelo para abrir o gerador, explore o catálogo completo ou crie novos templates para reutilizar no dia a dia.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="tour-uploaded-documents"]',
      meta: { requiresSidebar: true, requiresDocumentsOpen: true },
      popover: {
        title: 'Enviar para Assinatura',
        description:
          'Em <strong>Enviar para Assinatura</strong> você envia um ou mais PDFs que já estão prontos (até 5 MB cada, com limite total de 100 MB por conta). Na mesma tela você acompanha a lista, baixa arquivos, posiciona campos de assinatura e envia pelo fluxo <strong>Assinafy</strong> — usando a mesma licença e limites de assinatura dos demais documentos, sem precisar redigir no editor.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="tour-crm"]',
      meta: { requiresSidebar: true, requiresCrmOpen: true },
      popover: {
        title: 'CRM Neo Documentos',
        description:
          'O <strong>CRM Neo Documentos</strong> é o mesmo CRM da extensão do Chrome no WhatsApp — tudo sincronizado na nuvem. Aqui no sistema você gerencia o pipeline com mais espaço; na extensão, captura leads e usa respostas rápidas durante a conversa. Se ainda não instalou, use <strong>Baixe a nossa extensão</strong> no rodapé do menu.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="tour-crm-leads"]',
      meta: { requiresSidebar: true, requiresCrmOpen: true },
      popover: {
        title: 'Leads',
        description:
          'Em <strong>Leads</strong> você vê o funil em formato kanban (Novo, Qualificado, Visita, Proposta, Ganho e Perdido). Arraste cards entre colunas, filtre, edite observações e follow-ups. Leads criados no WhatsApp aparecem aqui automaticamente — e o contrário também.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="tour-crm-respostas"]',
      meta: { requiresSidebar: true, requiresCrmOpen: true },
      popover: {
        title: 'Respostas Padrão',
        description:
          'Em <strong>Respostas Padrão</strong> você organiza textos prontos por categoria, marca favoritos e usa variáveis como <strong>{{nome}}</strong> e <strong>{{empreendimento}}</strong>. O que você criar ou editar aqui fica disponível na extensão para inserir no chat do WhatsApp.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="tour-license-store"]',
      meta: { requiresSidebar: true },
      popover: {
        title: 'Comprar Licença',
        description:
          'Em <strong>Comprar Licença</strong> você escolhe um plano Individual ou Empresarial. Há duas formas de pagar: <strong>Compra Única</strong> (pagamento avulso) ou <strong>Assinar</strong> (cobrança mensal automática no cartão via Mercado Pago). Troca de plano também começa por aqui; o gerenciamento da assinatura fica em <strong>Minha Licença</strong>.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="tour-account-management"]',
      meta: { requiresSidebar: true },
      popover: {
        title: 'Minha Conta',
        description:
          'Em <strong>Minha Conta</strong> você gerencia o perfil e a segurança. Também é aqui (e pelo atalho na Home) que você <strong>conecta o WhatsApp</strong>: gera um código de vinculação e envia para o número do neo-modelos para usar o assistente pelo app.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="tour-notifications"]',
      meta: { requiresSidebar: true },
      popover: {
        title: 'Notificações',
        description:
          'Em <strong>Notificações</strong> ficam os avisos do sistema — por exemplo, atualizações sobre documentos, assinaturas ou eventos da sua conta. O selo numérico no menu indica quantas mensagens ainda não foram lidas.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="tour-configurations"]',
      meta: { requiresSidebar: true },
      popover: {
        title: 'Configurações',
        description:
          'Em <strong>Configurações</strong> você define a <strong>logo de cabeçalho</strong> e a <strong>logo de rodapé</strong> usadas nos documentos gerados. Assim, seus contratos saem com a identidade visual do seu escritório ou empresa.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="tour-license"]',
      meta: { requiresSidebar: true },
      popover: {
        title: 'Minha Licença',
        description:
          'Ao clicar em <strong>Minha Licença</strong>, você vê a licença ativa (limites, validade, uso e Neo Coins). Se você <strong>assinou um plano</strong> (Assinar na loja), também aparece o painel da assinatura com status, valor mensal e próxima cobrança — e é <strong>aqui que você cancela a assinatura</strong>, se quiser. Após cancelar, o acesso continua até o fim do período já pago.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="tour-customers"]',
      meta: { requiresSidebar: true },
      popover: {
        title: 'Clientes',
        description:
          'Em <strong>Clientes</strong> você cadastra as partes dos documentos (nome, CPF e dados cadastrais) para preencher contratos com mais rapidez. Isso é diferente dos <strong>Leads</strong> do CRM: clientes são para documentos; leads são o funil de vendas do WhatsApp.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="tour-chrome-extension"]',
      meta: { requiresSidebar: true },
      popover: {
        title: 'Baixe a nossa extensão',
        description:
          'Em <strong>Baixe a nossa extensão</strong> você abre a Chrome Web Store em uma nova aba. Essa é nossa extensão CRM para ser usada junto com o WhatsApp Web, integrada com o CRM Neo Documentos — captura leads e respostas rápidas na conversa, sincronizadas com o que você gerencia aqui no sistema.',
        side: 'right',
        align: 'end',
      },
    },
    {
      element: '[data-tour="tour-connect-whatsapp"]',
      meta: { closeSidebarOnMobile: true },
      popover: {
        title: 'Conectar WhatsApp',
        description:
          'Use <strong>Conectar WhatsApp</strong> para abrir o painel de vinculação. Gere um código, toque no link do número comercial (ou copie o código) e envie a mensagem no WhatsApp — assim o assistente reconhece sua conta. A mesma área existe em <strong>Minha Conta</strong>.',
        side: 'bottom',
        align: 'end',
      },
    },
    {
      element: '[data-tour="tour-create-from-model"]',
      meta: { closeSidebarOnMobile: true },
      popover: {
        title: 'Atalho: Criar a partir de Modelo',
        description:
          'Este botão na Home leva direto para a criação de documento a partir de um modelo — o caminho mais comum no dia a dia. Para explorar o catálogo completo, use a <strong>Biblioteca de Modelos</strong> no menu Documentos. Ao lado ficam <strong>Conectar WhatsApp</strong> e <strong>Mostrar tutorial</strong>.',
        side: 'bottom',
        align: 'end',
      },
    },
    {
      element: '[data-tour="tour-metrics"]',
      meta: { closeSidebarOnMobile: true },
      popover: {
        title: 'Resumo da conta e uso',
        description:
          'Estes cards mostram um resumo rápido: se você tem <strong>assinatura mensal</strong>, aparece o plano, o valor/mês e a próxima cobrança (com Neo Coins); caso contrário, o status da licença avulsa. Ao lado ficam documentos gerados e assinaturas na licença atual. Detalhes e cancelamento da assinatura ficam em <strong>Minha Licença</strong>.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="tour-documents-area"]',
      meta: { closeSidebarOnMobile: true },
      popover: {
        title: 'Lista e filtros de documentos',
        description:
          'Aqui você busca, filtra e organiza seus documentos por status (rascunho, PDF gerado, aguardando assinatura, assinado, etc.) e por tipo (todos, modelos do sistema, manuais ou seus modelos). Nesta área você também edita, baixa, gera PDF, envia para assinatura ou exclui documentos conforme o status de cada um.',
        side: 'top',
        align: 'start',
      },
    },
    {
      element: '[data-tour="tour-show-tutorial"]',
      meta: { closeSidebarOnMobile: true },
      popover: {
        title: 'Tutorial concluído',
        description:
          'Pronto! Você já conhece documentos, a biblioteca de modelos, o CRM Neo Documentos, a vinculação do WhatsApp, o atalho para baixar a extensão e o painel principal. Sempre que precisar, clique em <strong>Mostrar tutorial</strong> para repetir este tour. Bom trabalho com o neo-modelos.',
        side: 'bottom',
        align: 'end',
      },
    },
  ];
}
